import { Link } from 'react-router-dom';

export default function TermsPage() {
  return (
    <div className="min-h-screen bg-surface-900 text-surface-200">
      <div className="max-w-3xl mx-auto px-6 py-12">
        <Link to="/" className="text-brand-400 text-sm hover:underline mb-8 inline-block">&larr; Back to AutoCarPost</Link>
        <h1 className="text-3xl font-bold text-white mb-2">Terms of Service</h1>
        <p className="text-surface-500 text-sm mb-8">Last updated: {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</p>

        <div className="space-y-8 text-surface-300 text-sm leading-relaxed">
          <section>
            <h2 className="text-lg font-semibold text-white mb-3">1. Agreement to Terms</h2>
            <p>By accessing or using AutoCarPost ("the Service"), you agree to be bound by these Terms of Service. If you do not agree to these terms, do not use the Service. The Service is operated by AutoCarPost ("we", "us", or "our").</p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-white mb-3">2. Account Registration</h2>
            <p>To use certain features, you must create an account. You agree to:</p>
            <ul className="list-disc ml-6 mt-2 space-y-1">
              <li>Provide accurate and complete information during registration</li>
              <li>Maintain the security of your account credentials</li>
              <li>Notify us immediately of any unauthorized access to your account</li>
              <li>Accept responsibility for all activity that occurs under your account</li>
            </ul>
            <p className="mt-2">We offer two account types: <strong>Individual</strong> (personal car sellers) and <strong>Dealer</strong> (licensed dealerships). Dealer accounts have access to additional features including CRM, deal flow, and marketplace integrations.</p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-white mb-3">3. Free and Paid Services</h2>
            <p>AutoCarPost offers a free tier that includes basic vehicle listing capabilities. Paid tiers (Starter, Pro, Enterprise) provide additional features such as AI-powered descriptions, marketplace posting, and advanced analytics.</p>
            <ul className="list-disc ml-6 mt-2 space-y-1">
              <li>Free accounts may list up to 5 vehicles</li>
              <li>Paid subscriptions are billed monthly via Stripe</li>
              <li>You may cancel your subscription at any time through the billing portal</li>
              <li>Refunds are not provided for partial billing periods</li>
              <li>We reserve the right to modify pricing with 30 days notice</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-white mb-3">4. Vehicle Listings</h2>
            <p>When listing a vehicle on AutoCarPost, you represent and warrant that:</p>
            <ul className="list-disc ml-6 mt-2 space-y-1">
              <li>You are the legal owner of the vehicle or authorized to sell it</li>
              <li>All information provided (VIN, mileage, condition, price) is accurate to the best of your knowledge</li>
              <li>The vehicle is not stolen, salvaged without disclosure, or encumbered by undisclosed liens</li>
              <li>Photos uploaded are of the actual vehicle being listed</li>
            </ul>
            <p className="mt-2">We reserve the right to remove any listing that violates these terms or our Acceptable Use Policy.</p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-white mb-3">5. AI-Generated Content</h2>
            <p>AutoCarPost uses artificial intelligence to generate vehicle descriptions, market analysis, and pricing suggestions. You acknowledge that:</p>
            <ul className="list-disc ml-6 mt-2 space-y-1">
              <li>AI-generated descriptions are suggestions and should be reviewed before publishing</li>
              <li>Market pricing data and valuations are estimates and not guaranteed</li>
              <li>AI analysis of vehicle photos may contain inaccuracies</li>
              <li>You are ultimately responsible for the accuracy of your listings</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-white mb-3">6. Marketplace Posting</h2>
            <p>AutoCarPost may post your listings to third-party marketplaces (Facebook Marketplace, Craigslist, etc.) on your behalf. By using this feature, you:</p>
            <ul className="list-disc ml-6 mt-2 space-y-1">
              <li>Authorize us to post and manage listings on connected platforms</li>
              <li>Agree to comply with each platform's terms of service</li>
              <li>Acknowledge that we are not responsible for third-party platform actions, policies, or disputes</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-white mb-3">7. Intellectual Property</h2>
            <p>The AutoCarPost platform, including its design, code, and branding, is our intellectual property. You retain ownership of content you upload (photos, descriptions). By uploading content, you grant us a non-exclusive license to display, reproduce, and distribute it in connection with the Service.</p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-white mb-3">8. Limitation of Liability</h2>
            <p>AutoCarPost is provided "as is" without warranties of any kind. We are not liable for:</p>
            <ul className="list-disc ml-6 mt-2 space-y-1">
              <li>Transactions between buyers and sellers</li>
              <li>Accuracy of vehicle information, pricing, or valuations</li>
              <li>Third-party marketplace actions or downtime</li>
              <li>Loss of data or service interruptions</li>
              <li>Any indirect, incidental, or consequential damages</li>
            </ul>
            <p className="mt-2">Our total liability shall not exceed the amount you paid for the Service in the 12 months preceding the claim.</p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-white mb-3">9. Termination</h2>
            <p>We may suspend or terminate your account at our discretion if you violate these terms or our Acceptable Use Policy. Upon termination, your listings will be removed and your data will be deleted within 30 days, except as required by law.</p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-white mb-3">10. Governing Law</h2>
            <p>These terms are governed by the laws of the State of Tennessee. Any disputes shall be resolved in the courts of Shelby County, Tennessee.</p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-white mb-3">11. Changes to Terms</h2>
            <p>We may update these terms at any time. We will notify registered users of material changes via email. Continued use of the Service after changes constitutes acceptance of the updated terms.</p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-white mb-3">12. Contact</h2>
            <p>For questions about these Terms of Service, contact us at <a href="mailto:legal@autocarpost.com" className="text-brand-400 hover:underline">legal@autocarpost.com</a>.</p>
          </section>
        </div>

        <div className="mt-12 pt-8 border-t border-surface-800 flex gap-6 text-xs text-surface-500">
          <Link to="/privacy" className="hover:text-brand-400">Privacy Policy</Link>
          <Link to="/acceptable-use" className="hover:text-brand-400">Acceptable Use Policy</Link>
        </div>
      </div>
    </div>
  );
}
